import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../Store';
import type { CheckoutRequestState } from '../../types/order';

export interface OrderItem {
  id: string;
  name: string;
  price: number;
  imageUrl: string;
  menuOptionGroupId: string | null;
  menuId: string;
  menuOptionId: string | null;
  isActive: boolean;
  sortIndex: number;
  // 受注停止（売り切れ）中フラグ。BEが現在時刻で計算した値（BE ADR 0085 / FE ADR 0054）
  isSuspended?: boolean;
}

export interface OrderCategory {
  id: string;
  name: string;
  sortIndex: number;
  items: OrderItem[];
}

export const fetchOrderMenus = createAsyncThunk<
  { 
    storeName: string; 
    orderMenuTemplate?: 'IMAGE' | 'TEXT';
    menuCategories: OrderCategory[];
    menuOptions: {
      menuOptionGroups: {
        menuId: string;
        menuOptionGroup: {
          menuOptionGroupId: string;
          /** 必須グループかどうか。BE 未対応レスポンスでは欠落するため、欠落時は true（必須）として扱う */
          required?: boolean;
          menuOptions: {
            menuOptionId: string;
            menuOptionName: string;
            price: number | null;
            priceType: 'ADDITIONAL' | 'DISCOUNT' | null;
          }[];
        };
      }[];
    };
  },
  { active_store_id: string; active_dining_table_id: string }
>(
  'order/fetchOrderMenus',
  async ({ active_store_id, active_dining_table_id }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${active_store_id}/active-dining-table/${active_dining_table_id}/menus`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch order menu: ${errorText}`);
      }
      return (await response.json()) as { 
        storeName: string; 
        orderMenuTemplate?: 'IMAGE' | 'TEXT';
        menuCategories: OrderCategory[];
        menuOptions: {
          menuOptionGroups: {
            menuId: string;
            menuOptionGroup: {
              menuOptionGroupId: string;
              /** 必須グループかどうか。BE 未対応レスポンスでは欠落するため、欠落時は true（必須）として扱う */
              required?: boolean;
              menuOptions: {
                menuOptionId: string;
                menuOptionName: string;
                price: number | null;
                priceType: 'ADDITIONAL' | 'DISCOUNT' | null;
              }[];
            };
          }[];
        };
      };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

type UserType = 'customer' | 'clerk';

/**
 * BE の ErrorResponse ({status, message, timestamp}) からユーザー向け message を取り出す。
 * 売り切れ拒否（422、BE ADR 0085）などのメッセージをそのまま画面に出すために使う。
 */
const extractApiErrorMessage = (errorText: string, fallback: string): string => {
  try {
    const parsed = JSON.parse(errorText);
    if (parsed && typeof parsed.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // JSONでないレスポンス（ゲートウェイエラー等）は fallback に落とす
  }
  return fallback;
};

export const addCart = createAsyncThunk<
  { menuId: string; menuOptionId: string | null; quantity: number },
  { activeStoreId: string; activeDiningTableId: string; menuId: string; menuOptionId: string | null; quantity: number; userType?: UserType }
>(
  'order/addCart',
  async ({ activeStoreId, activeDiningTableId, menuId, menuOptionId, quantity, userType = 'customer' }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/add-cart/by-${userType}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ menuId, menuOptionId, quantity }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(extractApiErrorMessage(errorText, 'カートへの追加に失敗しました。'));
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchCart = createAsyncThunk<
  { items: { id: string; menuId: string; menuOptionId: string | null; quantity: number }[] },
  { activeStoreId: string; activeDiningTableId: string; userType?: UserType }
>(
  'order/fetchCart',
  async ({ activeStoreId, activeDiningTableId, userType = 'customer' }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/cart/by-${userType}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cart: ${errorText}`);
      }
      return (await response.json()) as { items: { id: string; menuId: string; menuOptionId: string | null; quantity: number }[] };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateCartItemQuantity = createAsyncThunk<
  void,
  { activeStoreId: string; activeDiningTableId: string; menuId: string; menuOptionId: string | null; quantity: number; userType?: UserType }
>(
  'order/updateCartItemQuantity',
  async ({ activeStoreId, activeDiningTableId, menuId, menuOptionId, quantity, userType = 'customer' }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/update-cart/by-${userType}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ menuId, menuOptionId, quantity }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update cart item quantity: ${errorText}`);
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const checkoutInOrder = createAsyncThunk<
  void,
  { activeStoreId: string; activeDiningTableId: string; userType?: UserType }
>(
  'order/checkoutInOrder',
  async ({ activeStoreId, activeDiningTableId, userType = 'customer' }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/checkout-in-order/by-${userType}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(extractApiErrorMessage(errorText, '注文の確定に失敗しました。もう一度お試しください。'));
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const callClerk = createAsyncThunk<
  void,
  { activeStoreId: string; activeDiningTableId: string }
>(
  'order/callClerk',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/call/on-clerk-call`,
        {
          method: 'PUT',
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to call clerk: ${errorText}`);
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// 会計リクエスト画面で顧客に提示する、店舗が有効化した支払方法の表示名一覧。
// 未設定店舗は BE 側で「現金」にフォールバックされる。
export const fetchPaymentMethods = createAsyncThunk<
  string[],
  { activeStoreId: string; activeDiningTableId: string }
>(
  'order/fetchPaymentMethods',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/payment-methods`,
        { method: 'GET' }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch payment methods: ${errorText}`);
      }
      return (await response.json()) as string[];
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// 顧客からの会計リクエスト（会計希望）。選択した支払方法を店員へ通知し、テーブルの会計リクエスト状態を REQUESTED にする。
// 応答は CheckoutRequestState（ADR 0068）。旧 BE は空ボディを返すため、その場合は null を返す。
// 受領済み（ACKNOWLEDGED）の再送は BE が 409 を返すので、その message をそのまま画面に出す。
export const requestCheckout = createAsyncThunk<
  CheckoutRequestState | null,
  { activeStoreId: string; activeDiningTableId: string; paymentMethod?: string }
>(
  'order/requestCheckout',
  async ({ activeStoreId, activeDiningTableId, paymentMethod }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/call/on-checkout-request`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentMethod: paymentMethod ?? null }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(extractApiErrorMessage(errorText, '会計リクエストの送信に失敗しました。もう一度お試しください。'));
      }
      const text = await response.text();
      if (!text) {
        return null;
      }
      return JSON.parse(text) as CheckoutRequestState;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/** セッションが終了している（404）ときに fetchCheckoutRequestState が reject する識別用メッセージ。 */
export const CHECKOUT_REQUEST_SESSION_NOT_FOUND = 'CHECKOUT_REQUEST_SESSION_NOT_FOUND';

// 現在の会計リクエスト状態を取得する。顧客画面の初期表示と、REQUESTED / ACKNOWLEDGED 中のポーリングに使う（ADR 0068）。
// セッション非アクティブ（および旧 BE の未実装）は 404 で、識別可能なメッセージで reject する。
export const fetchCheckoutRequestState = createAsyncThunk<
  CheckoutRequestState,
  { activeStoreId: string; activeDiningTableId: string }
>(
  'order/fetchCheckoutRequestState',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${activeStoreId}/active-dining-table/${activeDiningTableId}/checkout-request`,
        { method: 'GET' }
      );
      if (response.status === 404) {
        return rejectWithValue(CHECKOUT_REQUEST_SESSION_NOT_FOUND);
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch checkout request state: ${errorText}`);
      }
      return (await response.json()) as CheckoutRequestState;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);